
import { useCallback, useEffect, useState } from 'react';

import { WeatherDataRecord } from './types';

const WEATHER_URL = '/weather';
const SSE_URL = '/weather/sse';

const POLL_INTERVAL_MS = 60 * 1000;
const RETRY_INTERVAL_MS = 3 * 1000;

// Safari waits 60 s on a stalled request; give up much sooner.
const REQUEST_TIMEOUT_MS = 8 * 1000;

// A live stream the browser gave up on is reopened after 5 s, then 10, 20, ... up to a minute.
const SSE_RECONNECT_MIN_MS = 5 * 1000;
const SSE_RECONNECT_MAX_MS = 60 * 1000;

// After this many failed polls in a row the page shows "no connection" (about 3-20 s, depending on how they fail).
const OFFLINE_AFTER_FAILURES = 2;

// Time of a reading in ms; NaN for the empty record the server returns before the first upload.
function readingTime(reading: WeatherDataRecord) {
    return reading.dateUtc ? Date.parse(reading.dateUtc) : NaN;
}

// Whether a reading (from SSE or a poll) should replace the one on screen.
// The two transports can deliver out of order (a slow poll can land after a newer SSE event),
// so only a newer reading wins, and the empty record never replaces a real one.
export function isNewerReading(candidate: WeatherDataRecord, current: WeatherDataRecord | undefined) {
    if (current === undefined) {
        return true;
    }

    const candidateTime = readingTime(candidate);
    const currentTime = readingTime(current);

    if (isNaN(candidateTime)) {
        return false;
    }

    return isNaN(currentTime) || candidateTime > currentTime;
}

// Minimal shape check for data from the network: a JSON object.
function isReading(value: unknown): value is WeatherDataRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export interface WeatherDataState {
    data: WeatherDataRecord | undefined; // the latest reading, undefined until the first one arrives
    offline: boolean; // the server stopped answering; data (if any) is the last one received
}

// The server pushes new readings over SSE; a poll every minute is the fallback and the connection check.
export function useWeatherData(): WeatherDataState {
    const [weatherData, setWeatherData] = useState<WeatherDataRecord>();
    const [failedPolls, setFailedPolls] = useState(0);

    // The only way a reading gets on screen, from either transport.
    const acceptReading = useCallback((reading: WeatherDataRecord) => {
        setWeatherData(current => isNewerReading(reading, current) ? reading : current);
        setFailedPolls(0);
    }, []);

    useEffect(() => {
        let cancelled = false;
        let polling = false;
        let timeoutID: ReturnType<typeof setTimeout> | undefined;

        // Load now, then refresh every minute.
        // A failed or stalled request is retried after a few seconds instead of a full minute.
        const poll = async () => {
            if (polling) {
                return;
            }

            clearTimeout(timeoutID);
            polling = true;
            const reading = await fetchWeatherData();
            polling = false;

            if (cancelled) {
                return;
            }

            if (reading) {
                acceptReading(reading);
            } else {
                setFailedPolls(n => n + 1);
            }

            timeoutID = setTimeout(poll, reading ? POLL_INTERVAL_MS : RETRY_INTERVAL_MS);
        };

        let eventSource: EventSource | undefined;
        let reconnectID: ReturnType<typeof setTimeout> | undefined;
        let reconnectDelay = SSE_RECONNECT_MIN_MS;

        const connect = () => {
            const source = new EventSource(SSE_URL);
            eventSource = source;

            source.onmessage = (event) => {
                reconnectDelay = SSE_RECONNECT_MIN_MS;

                const reading = parseReading(event.data);
                if (reading) {
                    acceptReading(reading);
                } else {
                    console.warn('SSE: invalid data', event.data);
                }
            };

            // The stream dropped. Check the server now instead of at the next scheduled poll,
            // so a dead connection shows up within seconds.
            source.onerror = () => {
                poll();

                // EventSource reconnects by itself after a network drop, but gives up for good after an error
                // response (e.g. a proxy's 502). Then open a new one, waiting longer each time it fails.
                if (source.readyState === EventSource.CLOSED) {
                    clearTimeout(reconnectID);
                    reconnectID = setTimeout(connect, reconnectDelay);
                    reconnectDelay = Math.min(reconnectDelay * 2, SSE_RECONNECT_MAX_MS);
                }
            };
        };

        connect();
        poll();

        return () => {
            cancelled = true;
            clearTimeout(timeoutID);
            clearTimeout(reconnectID);
            eventSource?.close();
        };
    }, [acceptReading]);

    return { data: weatherData, offline: failedPolls >= OFFLINE_AFTER_FAILURES };
}

function parseReading(json: string): WeatherDataRecord | null {
    try {
        const value: unknown = JSON.parse(json);
        return isReading(value) ? value : null;
    } catch {
        return null;
    }
}

async function fetchWeatherData(): Promise<WeatherDataRecord | null> {
    // AbortController instead of AbortSignal.timeout() for older iOS versions.
    const controller = new AbortController();
    const abortID = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
        const response = await fetch(WEATHER_URL, { signal: controller.signal, cache: 'no-store' });
        if (!response.ok) {
            console.warn(`Periodic: HTTP ${response.status}`);
            return null;
        }

        const reading = parseReading(await response.text());
        if (!reading) {
            console.warn('Periodic: invalid data');
        }

        return reading;
    } catch (err) {
        console.warn('Periodic: request failed', err);
        return null;
    } finally {
        clearTimeout(abortID);
    }
}
